import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getChapterById, createChapter, updateChapter, getNovelById } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Save } from 'lucide-react';

const ChapterEditor = () => {
  const { id, novelId: newNovelId } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'new';

  const [novel, setNovel] = useState(null);
  const [formData, setFormData] = useState({
    novelId: newNovelId || '',
    chapterNumber: 1,
    title: '',
    content: '',
    publishedDate: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      fetchChapter();
    } else if (newNovelId) {
      fetchNovel(newNovelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, newNovelId]);

  const fetchNovel = async (nId) => {
    try {
      const data = await getNovelById(nId);
      setNovel(data);
      setFormData(prev => ({
        ...prev,
        chapterNumber: data.totalChapters + 1
      }));
    } catch (error) {
      console.error('Error fetching novel:', error);
    }
  };

  const fetchChapter = async () => {
    try {
      const data = await getChapterById(id);
      const novelData = await getNovelById(data.novelId);
      setNovel(novelData);
      setFormData({
        novelId: data.novelId,
        chapterNumber: data.chapterNumber,
        title: data.title,
        content: data.content,
        publishedDate: data.publishedDate
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load chapter",
        variant: "destructive"
      });
      navigate('/admin/novels');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await updateChapter(id, formData);
        toast({
          title: "Chapter updated",
          description: "Your changes have been saved."
        });
      } else {
        await createChapter(formData);
        toast({
          title: "Chapter created",
          description: "Your chapter has been published."
        });
      }
      navigate(`/admin/novel/chapters/${formData.novelId}`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save chapter.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link to={`/admin/novel/chapters/${formData.novelId}`} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={16} />Back to Chapters
          </Link>
          <h1 className="text-2xl font-serif text-gray-900">
            {isEditing ? 'Edit Chapter' : 'Create New Chapter'}
          </h1>
          {novel && <p className="text-sm text-gray-600 mt-1">Novel: {novel.title}</p>}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="chapterNumber" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Chapter Number *
              </label>
              <Input type="number" id="chapterNumber" name="chapterNumber" value={formData.chapterNumber} onChange={handleChange} required min="1" />
            </div>

            <div>
              <label htmlFor="publishedDate" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                Published Date *
              </label>
              <Input type="date" id="publishedDate" name="publishedDate" value={formData.publishedDate} onChange={handleChange} required />
            </div>
          </div>

          <div>
            <label htmlFor="title" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Chapter Title *
            </label>
            <Input type="text" id="title" name="title" value={formData.title} onChange={handleChange} required placeholder="Enter chapter title" />
          </div>

          <div>
            <label htmlFor="content" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Content *
            </label>
            <Textarea id="content" name="content" value={formData.content} onChange={handleChange} required rows={25} placeholder="Write your chapter content here..." className="font-serif" />
            <p className="text-xs text-gray-500 mt-2">
              Tip: Use double line breaks to separate paragraphs
            </p>
          </div>

          <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
            <Button type="submit" disabled={saving} className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800">
              <Save size={16} />
              {saving ? 'Saving...' : (isEditing ? 'Update Chapter' : 'Create Chapter')}
            </Button>
            <Link to={`/admin/novel/chapters/${formData.novelId}`}>
              <Button type="button" variant="outline">Cancel</Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChapterEditor;